import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Container,
  FormControl,
  makeStyles,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@material-ui/core';
import { ActivePane, useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { SmallError } from '../../../icons/SmallError';
import { useCameraTest } from './useCameraTest/useCameraTest';
import useDevices from '../../../hooks/useDevices/useDevices';
import { PrimaryButton } from '../../common/PrimaryButton';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
  paper: {
    padding: '2em',
    borderRadius: '8px',
    '@media screen and (max-height: 400px)': {
      padding: '2px',
    },
  },
  videoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  aspectRatioContainer: {
    position: 'relative',
    display: 'flex',
    width: '80%',
    padding: '1em',
    margin: '1em 0',
    '&::after': {
      content: '""',
      paddingTop: '56.25%',
    },
    '& video': {
      position: 'absolute',
      height: '100%',
      width: '100%',
      objectFit: 'cover',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    margin: '0.5em 0',
    '& svg': {
      marginRight: '0.3em',
    },
  },
  gridContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
    '@media screen and (max-width: 950px)': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  mainGridContainer: {
    maxWidth: '95%',
    '@media screen and (max-width: 950px)': {
      paddingTop: '10px',
    },
  },
});

export function CameraTest() {
  const classes = useStyles();
  const { videoInputDevices } = useDevices();
  const { state, dispatch } = useAppStateContext();
  const { videoElementRef, startVideoTest, stopVideoTest, videoTest, videoTestError } = useCameraTest();

  const prevVideoDeviceID = useRef('');
  const [videoInputDeviceID, setVideoInputDeviceID] = useState('');
  const { t } = useTranslation('', { keyPrefix: 'CAMERA' });

  const setDevice = (deviceID: string) => {
    setVideoInputDeviceID(deviceID);
  };

  useEffect(() => {
    // Stop the test when we are not on the CameraTest pane and there is an active test
    if (state.activePane !== ActivePane.CameraTest && videoTest) {
      stopVideoTest();
      prevVideoDeviceID.current = '';
    }
  }, [state.activePane, stopVideoTest, videoTest]);

  useEffect(() => {
    // Start the test when we are on the CameraTest pane and when the videoInputDeviceID changes
    if (state.activePane === ActivePane.CameraTest) {
      const newDeviceSelected = prevVideoDeviceID.current !== videoInputDeviceID;
      prevVideoDeviceID.current = videoInputDeviceID;

      if (videoInputDeviceID && newDeviceSelected) {
        startVideoTest(videoInputDeviceID);
      }

      if (videoTestError) {
        stopVideoTest();
      }
    }
  }, [state.activePane, videoInputDeviceID, startVideoTest, stopVideoTest, videoTestError, dispatch]);

  useEffect(() => {
    // If no device is select, set the first available device as the active device.
    const hasSelectedDevice = videoInputDevices.some((device) => device.deviceId === videoInputDeviceID);
    if (videoInputDevices.length && !hasSelectedDevice) {
      setVideoInputDeviceID(videoInputDevices[0].deviceId);
    }
  }, [videoInputDevices, videoInputDeviceID]);

  return (
    <Container>
      <Grid container className={classes.gridContainer}>
        <Grid item md={5}>
          <Typography variant="h1" gutterBottom>
            {t('CHECK_YOUR_VIDEO')}
          </Typography>

          <Typography variant="body1" gutterBottom>
            {t('CAMERA_SET')}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>{t('DOES_YOUR_VIDEO_LOOK_OK')}</strong>
          </Typography>

          <PrimaryButton
            onClick={() => dispatch({ type: 'next-pane' })}
            style={{ marginRight: '1.5em', borderRadius: '6px' }}
            disabled={!!videoTestError}
          >
            {t('YES')}
          </PrimaryButton>
          <Button
            color="primary"
            disableRipple
            onClick={() => dispatch({ type: 'next-pane' })}
            disabled={!!videoTestError}
          >
            {t('SKIP_FOR_NOW')}
          </Button>
        </Grid>

        <Grid item md={5} className={classes.mainGridContainer}>
          <Paper className={classes.paper}>
            <Grid container className={classes.gridContainer}>
              <Typography variant="subtitle2">
                <strong>{t('VIDEO_PREVIEW')}</strong>
              </Typography>
              <div className={classes.aspectRatioContainer}>
                <video ref={videoElementRef} />
              </div>
            </Grid>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                <strong>{t('CAMERA')}</strong>
              </Typography>
              <Select
                onChange={(e) => setDevice(e.target.value as string)}
                value={videoInputDeviceID}
                variant="outlined"
                disabled={!!videoTestError}
              >
                {videoInputDevices.map((device) => (
                  <MenuItem value={device.deviceId} key={device.deviceId}>
                    {device.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {videoTestError && (
              <div className={classes.error}>
                <SmallError />
                <Typography variant="subtitle2" color="error">
                  {t('UNABLE_TO_CONNECT')}
                </Typography>
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
